import { env } from "$env/dynamic/public";
import type { Version } from "./env.server";

//#######################################################################################
//
// This file is to PUBLIC variables.
//
//#######################################################################################

export type ID = 'QA' | 'PP' | 'PROD';

const TRUES = ['1', 'yes', 'true'];
const isTrue = (str:string) => TRUES.includes(str?.toLowerCase());
const toNum = (str:string) => !str?.length ? 0 : parseFloat(str);

const preset_id = (env.PUBLIC_PRESET || env.PUBLIC_ENV || 'QA').toUpperCase() as ID | 'MOCK' | 'DEV';
export const MOCK = preset_id === 'MOCK';
export const DEV = preset_id === 'DEV';

let validPreset = true;

if (MOCK) {
	console.warn('====  MOCK MODE ENABLED  ====');
	console.info('Running in mock mode is intended to facilitate automated testing by providing real but predictable data. It is not designed for manual testing, nor is it suitable for production environments.');
} else if (DEV) {
	console.warn('====  DEV MODE ENABLED  ====');
	console.info('Development mode is like a QA environment, but with some integration services disabled by default. These may include Sentry, GA4, and others.');
} else if (preset_id !== 'QA' && preset_id !== 'PP' && preset_id !== 'PROD') {
	console.warn(`Invalid preset ID: ${preset_id}. Defaulting to QA.`);
	validPreset = false;
}

const id = (MOCK || DEV) ? 'QA' : validPreset ? preset_id as ID : 'QA';
export const ENV = id;

export const ENABLE_API_PROXY = Boolean(env.PUBLIC_ENABLE_API_PROXY) ?? false;

export const API_HOST = env.PUBLIC_API_HOST ?? {
	QA: 'https://api.qa.mrd.com',
	PP: 'https://api.pp.mrd.com',
	PROD: 'https://api.mrdfood.com',
}[id];

export const CA_API_HOST = env.PUBLIC_CA_API_HOST ?? {
	QA: 'https://ca-api.qa.mrd.com',
	PP: 'https://ca-api.pp.mrd.com',
	PROD: 'https://ca-api.mrdfood.com',
}[id];

export const PNP_IMG_HOST = env.PUBLIC_PNP_IMG_HOST ?? {
	QA: 'https://images.dev.bottles.bottlesapp.co.za/productImages',
	PP: 'https://images.qa.bottles.bottlesapp.co.za/productImages',
	PROD: 'https://images.bottles.bottlesapp.co.za/productImages',
}[id];

export const PNP_IMGHD_HOST = env.PUBLIC_PNP_IMGHD_HOST ?? {
	QA: 'https://images.dev.bottles.bottlesapp.co.za/highResProductImages',
	PP: 'https://images.qa.bottles.bottlesapp.co.za/highResProductImages',
	PROD: 'https://images.bottles.bottlesapp.co.za/highResProductImages',
}[id];

export const IMG_HOST = env.PUBLIC_IMG_HOST ?? {
	QA: 'https://img.qa.mrd.com',
	PP: 'https://img.pp.mrd.com',
	PROD: 'https://img.mrdfood.com',
}[id];

export const CDN_HOST = env.PUBLIC_CDN_HOST ?? {
	QA: 'https://static.mrd.com',
	PP: 'https://static.mrd.com',
	PROD: 'https://static.mrd.com',
}[id];

export const SEO_HOST = env.PUBLIC_SEO_HOST ?? {
	QA: 'https://www.qa.mrd.com',
	PP: 'https://www.pp.mrd.com',
	PROD: 'https://www.mrdfood.com',
}[id];

export const V1_HOST = env.PUBLIC_V1_HOST ?? {
	QA: 'https://www.qa.mrd.com',
	PP: 'https://www.pp.mrd.com',
	PROD: 'https://www.mrdfood.com',
}[id];

export const ZENDESK_HOST = env.PUBLIC_ZENDESK_HOST ?? {
	QA: 'https://mrdfood461247963.zendesk.com', // TODO: update to mrd1730099995
	PP: 'https://mrdfood461247963.zendesk.com', // TODO: update to mrd1730099995
	PROD: 'https://mrdfood.zendesk.com',
}[id];

/**
 * The category ID for articles related to general support.
 */
export const ZENDESK_CATEGORY_GENERAL = env.PUBLIC_ZENDESK_CATEGORY_GENERAL ?? {
	QA: '115001592748', // new UAT value: 16924435264284
	PP: '115001592748', // new UAT value: 16924435264284
	PROD: '360000025268',
}[id];

/**
 * The category ID for articles related to orders.
 */
export const ZENDESK_CATEGORY_ORDERS = env.PUBLIC_ZENDESK_CATEGORY_ORDERS ?? {
	QA: '115001597947', // new UAT value: 16924443968668
	PP: '115001597947', // new UAT value: 16924443968668
	PROD: '360000022987',
}[id];

/**
 * The section ID for articles related to orders in progress.
 */
export const ZENDESK_SECTION_ORDER_IN_PROGRESS = env.PUBLIC_ZENDESK_SECTION_ORDER_IN_PROGRESS ?? {
	QA: '115002817948', // new UAT value: 16924445511964
	PP: '115002817948', // new UAT value: 16924445511964
	PROD: '360000038188',
}[id];

/**
 * The section ID for articles related to completed orders.
 */
export const ZENDESK_SECTION_ORDER_COMPLETED = env.PUBLIC_ZENDESK_SECTION_ORDER_COMPLETED ?? {
	QA: '115003363067', // new UAT value: 16924490975004
	PP: '115003363067', // new UAT value: 16924490975004
	PROD: '360000037927',
}[id];

export const PAYU_ENV = env.PUBLIC_PAYU_ENV ?? {
	QA: 'test',
	PP: 'test',
	PROD: 'live',
}[id];

export const PAYU_PK = env.PUBLIC_PAYU_PK ?? {
	QA: '4601e599-dc0d-41b9-9f3b-db5e555661e9',
	PP: '4601e599-dc0d-41b9-9f3b-db5e555661e9',
	PROD: '66dfcabf-734d-447a-967b-a51b5bd14037',
}[id];

export const SENDBIRD_APPID = env.PUBLIC_SENDBIRD_APPID ?? {
	QA: 'C3E80860-451A-4BA2-AE76-A14B49059F35',
	PP: 'C3E80860-451A-4BA2-AE76-A14B49059F35',
	PROD: 'B8360780-E38D-492E-995F-C92F6AF2E78C',
}[id];

export const GMAPS_API_KEY = env.PUBLIC_GMAPS_API_KEY ?? {
	QA: 'AIzaSyAsPS4V5aAWzS50ZDpaxUtmotyoeUFRiJY',
	PP: 'AIzaSyAsPS4V5aAWzS50ZDpaxUtmotyoeUFRiJY',
	PROD: 'AIzaSyAltMDrv0SgSDo5NXf5i3IPyIXtm2hPYOw',
}[id];

export const GMAPS_LIGHT_MAP_ID = env.PUBLIC_GMAPS_LIGHT_MAP_ID ?? {
	QA: 'c3a529b7a71b44f4',
	PP: '1b0aa22bddcce490',
	PROD: '674d84bab1243933',
}[id];

export const GMAPS_DARK_MAP_ID = env.PUBLIC_GMAPS_DARK_MAP_ID ?? {
	QA: '22030bb6132d15e0',
	PP: '6a4273a30e22e2d1',
	PROD: 'e085278899bc1ab',
}[id];

export const FIREBASE_API_KEY = env.PUBLIC_FIREBASE_API_KEY ?? {
	QA: 'AIzaSyDol1GJR0myfynTGuHLnf2HITViVHpYfqg',
	PP: 'AIzaSyBUkMvePdEeLkzP7-wtUMt1pLkuRwsOegE',
	PROD: 'AIzaSyAnH3wtXC_nMArFmSrOxT_BXKM1nemq1_c',
}[id];

export const FIREBASE_AUTH_DOMAIN = env.PUBLIC_FIREBASE_AUTH_DOMAIN ?? {
	QA: 'mr-d-food-qa.firebaseapp.com',
	PP: 'mr-d-food-pp.firebaseapp.com',
	PROD: 'mr-d-food-app.firebaseapp.com',
}[id];

export const FIREBASE_DATABASE_URL = env.PUBLIC_FIREBASE_DATABASE_URL ?? {
	QA: 'https://mr-d-food-qa.firebaseio.com',
	PP: 'https://mr-d-food-pp.firebaseio.com',
	PROD: 'https://mr-d-food-app.firebaseio.com',
}[id];

export const FIREBASE_PROJECT_ID = env.PUBLIC_FIREBASE_PROJECT_ID ?? {
	QA: 'mr-d-food-qa',
	PP: 'mr-d-food-pp',
	PROD: 'mr-d-food-app',
}[id];

export const FIREBASE_STORAGE_BUCKET = env.PUBLIC_FIREBASE_STORAGE_BUCKET ?? {
	QA: 'mr-d-food-qa.appspot.com',
	PP: 'mr-d-food-pp.appspot.com',
	PROD: 'mr-d-food-app.appspot.com',
}[id];

export const FIREBASE_MESSAGING_SENDER_ID = env.PUBLIC_FIREBASE_MESSAGING_SENDER_ID ?? {
	QA: '554668785649',
	PP: '723738481382',
	PROD: '1093755597615',
}[id];

export const FIREBASE_APP_ID = env.PUBLIC_FIREBASE_APP_ID ?? {
	QA: '1:554668785649:web:240b0cd06711df64478e50',
	PP: '1:723738481382:web:91a03f4f064321ec99a127',
	PROD: '1:1093755597615:web:85a1236a340b2e7a57a7da',
}[id];

export const FIREBASE_MEASUREMENT_ID = env.PUBLIC_FIREBASE_MEASUREMENT_ID ?? {
	QA: 'G-C7TPVR2Z74',
	PP: 'G-PYP7RYX9FC',
	PROD: 'G-MST7YH9HSR',
}[id];

export const FIREBASE_CLOUD_MESSAGING_VAPID = env.PUBLIC_FIREBASE_CLOUD_MESSAGING_VAPID ?? {
	QA: 'BGGBW6hbUC0fONHUCusY_2dRRfnUW2mkvh_zcXulyT2mU5KkQHQbq7tfx0xWGkvpkFZMo25ICFl7Sj11x_LTH8s',
	PP: 'BEPzRBsu2kXrIxyFyjWZkJeaqmBNryskv3PkSAJ24aET_5nDj1CeyDVaFuMyKKn4nYacRdYIyK06XJR1VlRQKl4',
	PROD: 'BFN6Ha3P6wiMKKT1p6-oIC_CwnZsVgYlenn6ljorrEXy4nOhW2BhRskqwBcDsVOtI69El_lngKdpaC08Bo7dwYA',
}[id];

export const APPLE_APP_ID = env.PUBLIC_APPLE_APP_ID ?? {
	QA: 'BU9HG54H24.com.mrd.MrDeliveryQA',
	PP: 'BU9HG54H24.com.mrd.MrDeliveryPREPROD',
	PROD: 'BU9HG54H24.com.mrd.MrDelivery',
}[id];

export const ANDROID_PACKAGE_NAME = env.PUBLIC_ANDROID_PACKAGE_NAME ?? {
	QA: 'com.mrd.food.qa',
	PP: 'com.mrd.food.preprod',
	PROD: 'com.mrd.food',
}[id];

export const ANDROID_CERT = env.PUBLIC_ANDROID_CERT ?? {
	QA: '20:DD:A9:14:83:27:87:2B:7A:2B:D2:63:4D:7E:FC:0B:ED:44:9E:5B:08:54:2D:45:FA:4E:DB:35:90:32:C0:A8',
	PP: 'D1:81:39:AC:57:61:B8:4B:C0:42:62:83:40:11:28:AB:0F:5A:BE:2E:0F:D4:93:1B:43:52:2B:D7:73:0A:D6:03',
	PROD: '54:C6:0A:3C:96:52:9B:F4:F9:0E:89:F3:10:FF:E6:FB:7F:F7:45:80:2D:F3:26:E8:20:E4:35:A5:2C:9A:1A:0D',
}[id];

export const GTM_ID = env.PUBLIC_GTM_ID ?? {
	QA: 'GTM-N3NVXDHJ',
	PP: 'GTM-W5KMV4TC',
	PROD: 'GTM-K5VFR64X',
}[id];

const _SENTRY_LOG_RATE_CLIENT = env.PUBLIC_SENTRY_LOG_RATE_CLIENT ?? {
	QA: DEV ? '0' : '1',
	PP: '1',
	PROD: '0.01',
}[id] ?? '1';
export const SENTRY_LOG_RATE_CLIENT = toNum(_SENTRY_LOG_RATE_CLIENT);

const _SENTRY_LOG_CSP_CLIENT = env.PUBLIC_SENTRY_LOG_CSP_CLIENT ?? {
	QA: 'true',
	PP: 'true',
	PROD: 'false',
}[id];
export const SENTRY_LOG_CSP_CLIENT = isTrue(_SENTRY_LOG_CSP_CLIENT);

// This has to equal the key in secrets manager - we need this at build time. See app.html and hooks.server.ts
export const RECAPTCHA_KEY = env.PUBLIC_RECAPTCHA_KEY ?? {
	QA: '6LfLRp4UAAAAABX08Zn8LADblwuPHE-1H4UNzVpJ',
	PP: '6LfLRp4UAAAAABX08Zn8LADblwuPHE-1H4UNzVpJ',
	PROD: '6LfLRp4UAAAAABX08Zn8LADblwuPHE-1H4UNzVpJ',
}[id];

const _ALLOW_CRAWLERS = env.PUBLIC_ALLOW_CRAWLERS ?? {
	QA: 'false',
	PP: 'false',
	PROD: 'true',
}[id];
export const ALLOW_CRAWLERS = isTrue(_ALLOW_CRAWLERS);


//#######################################################################################
// Set version details
//#######################################################################################

const _mockVersion = {} as Version;

let _version: Version | undefined;
if (MOCK) _version = _mockVersion;

let _resolveVerssionPromise: (version: Version) => void;
export const promiseVersion = (MOCK) ? Promise.resolve(_mockVersion) : new Promise<Version>((resolve) => _resolveVerssionPromise = resolve);

export function setVersion(version: Version) {
	if (_version) {
		console.warn('Version already set. Ignoring new version.');
		return;
	}
	_version = version;
	_resolveVerssionPromise(version);
	if (!DEV && !MOCK) console.log(`Version ${version.build.triggerName} [${version.commit.sha.substring(0,7)}]`);
}
export function getVersion(): Version | undefined {
	return _version;
}
